import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppThunkAPIType } from "../../app/appTypes";
import { getWithAuth } from "../../http";

export interface AICallMetrics {
    id: number;
    companyId: number;
    userId: string;
    occurredAt: Date;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    traceId: string;
    promptKey: string;
}

export interface AIRequestTrace {
    id: string;
    systemPrompt: string;
    userMessage: string;
    responseText: string;
}

export interface AICallMetricsRequestParams {
    companyId?: string;
    dateFrom?: Date;
    dateTo?: Date;
}

export interface AICallMetricsClientRequestParams {
    dateFrom?: Date;
    dateTo?: Date;
}

const buildQueryString = (params: Record<string, string | undefined>): string => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
    });
    const query = queryParams.toString();
    return query ? `?${query}` : '';
};

export const fetchAIRequests = createAsyncThunk<
    AICallMetrics[],
    AICallMetricsRequestParams,
    AppThunkAPIType
>(
    "ai/requests/fetch",
    async (params, { dispatch, rejectWithValue }) => {
        const queryParams: Record<string, string | undefined> = {};
        
        if (params.companyId && params.companyId !== 'all') {
            queryParams.companyId = params.companyId;
        }
        if (params.dateFrom) {
            queryParams.dateFrom = params.dateFrom.toISOString();
        }
        if (params.dateTo) {
            queryParams.dateTo = params.dateTo.toISOString();
        }

        const result = await dispatch(getWithAuth({
            url: `api/sys/ai-requests${buildQueryString(queryParams)}`,
        }));

        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as AICallMetrics[];
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const fetchAITrace = createAsyncThunk<
    AIRequestTrace,
    string,
    AppThunkAPIType
>(
    "ai/trace/fetch",
    async (traceId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/sys/ai-requests/trace/${traceId}`,
        }));

        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as AIRequestTrace;
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);


export const fetchAIRequestsForCompany = createAsyncThunk<
    AICallMetrics[],
    AICallMetricsClientRequestParams,
    AppThunkAPIType
>(
    "ai/requests/fetchForCompany",
    async (params, { dispatch, rejectWithValue }) => {
        const queryParams: Record<string, string | undefined> = {};
        
        if (params.dateFrom) {
            queryParams.dateFrom = params.dateFrom.toISOString();
        }
        if (params.dateTo) {
            queryParams.dateTo = params.dateTo.toISOString();
        }

        const result = await dispatch(getWithAuth({
            url: `api/ai-requests${buildQueryString(queryParams)}`,
        }));

        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as AICallMetrics[];
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const fetchAITraceForCompany = createAsyncThunk<
    AIRequestTrace,
    string,
    AppThunkAPIType
>(
    "ai/trace/fetchForCompany",
    async (traceId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/ai-requests/trace/${traceId}`,
        }));

        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as AIRequestTrace;
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);